import { ImagesState } from '../../../../store/types/template';
import { IMAGE_CONFIG } from './imageVisibilityConfig';
import {
  TemplateType,
  Platform,
  ImageField,
  AppleEnhancedDesignState,
} from '../../../../store/types/template';
import { getImageDimensions } from './imageDimensions';
import { APPLE_ENHANCED_DESIGN_IMAGES_NAMESPACE } from './appleEnhancedDesignConstants';
import { GOOGLE_DEFAULT_WAVE_BANNER_BASE64 } from '../../../../utils/base64';

export const isImageVisible = (
  templateType: TemplateType,
  platform: Platform,
  field: ImageField
): boolean => {
  return !!IMAGE_CONFIG[templateType]?.[platform]?.[field]?.visible;
};

export const isImageRequired = (
  templateType: TemplateType,
  platform: Platform,
  field: ImageField,
  images: ImagesState
): boolean => {
  const rule = IMAGE_CONFIG[templateType]?.[platform]?.[field]?.requiredIf;
  if (!rule?.unlessAllOf) return false;

  return rule.unlessAllOf.some((path) => {
    const [plat, fld] = path.split('.') as [Platform, ImageField];
    const value = images[plat]?.[fld];
    return !value || value === GOOGLE_DEFAULT_WAVE_BANNER_BASE64;
  });
};

// Google renders its class logo from the shared icon, not the shared logo.
const SHARED_SOURCE_BY_PLATFORM: Record<
  'apple' | 'google',
  Partial<Record<ImageField, ImageField>>
> = {
  apple: { logo: 'logo', icon: 'icon', banner: 'banner' },
  google: { logo: 'icon', wideLogo: 'logo', banner: 'banner' },
};

export const getSharedSourceField = (
  platform: Platform,
  field: ImageField
): ImageField | null => {
  if (platform === 'shared') return null;
  return SHARED_SOURCE_BY_PLATFORM[platform][field] ?? null;
};

export const getSharedFallbackImage = (
  images: ImagesState,
  platform: Platform,
  field: ImageField
): string => {
  const sharedField = getSharedSourceField(platform, field);
  if (!sharedField) return '';
  return (images.shared as Record<string, string>)?.[sharedField] || '';
};

export const getEffectiveTemplateImage = (
  images: ImagesState,
  platform: Platform,
  field: ImageField
): string => {
  const own = (images[platform] as Record<string, string>)?.[field] || '';
  if (own) return own;
  return getSharedFallbackImage(images, platform, field);
};

export const isUsingSharedFallback = (
  images: ImagesState,
  platform: Platform,
  field: ImageField
): boolean => {
  const own = (images[platform] as Record<string, string>)?.[field] || '';
  return !own && !!getSharedFallbackImage(images, platform, field);
};

export const getRevalidationTargets = (
  templateType: TemplateType,
  platform: Platform,
  field: ImageField
): string[] => {
  const affects =
    IMAGE_CONFIG[templateType]?.[platform]?.[field]?.affects || [];
  return Array.from(new Set(affects));
};

export const getDisabledByChange = (
  templateType: TemplateType,
  platform: Platform,
  field: ImageField
): string[] => {
  return IMAGE_CONFIG[templateType]?.[platform]?.[field]?.disables || [];
};

export const cleanImagesByTemplateType = (
  images: ImagesState,
  templateType: TemplateType,
  watermarkEnabled: boolean
): ImagesState => {
  const config = IMAGE_CONFIG[templateType];

  const resetPlatform = (platform: Platform) => {
    const current = images[platform] || {};
    const platformConfig = (config[platform] ?? {}) as Record<
      string,
      { visible?: boolean }
    >;
    const next: Record<string, string> = {};

    Object.entries(current).forEach(([key, value]) => {
      // Preserve watermark-related fields
      if (key === 'watermarkColor') {
        next[key] =
          watermarkEnabled && platformConfig?.banner?.visible
            ? value || 'light'
            : '';
        return;
      }
      if (key === 'watermarkPosition') {
        next[key] =
          watermarkEnabled && platformConfig?.banner?.visible
            ? value || 'bottomRight'
            : '';
        return;
      }

      // Keep only visible fields
      const fieldConfig = platformConfig[key as ImageField];
      if (fieldConfig?.visible) {
        next[key] = value;
      } else {
        next[key] = '';
      }
    });

    // Also ensure that visible fields that were missing are initialised
    Object.keys(platformConfig).forEach((fieldKey) => {
      if (!(fieldKey in next) && platformConfig[fieldKey]?.visible) {
        next[fieldKey] = '';
      }
    });

    return next;
  };

  return {
    shared: resetPlatform('shared'),
    apple: resetPlatform('apple'),
    google: resetPlatform('google'),
  };
};

export const validateImageDimensions = async (
  images: ImagesState,
  templateType?: TemplateType,
  appleEnhancedDesign?: AppleEnhancedDesignState,
  expressivePassEnabled?: boolean
): Promise<Record<string, string>> => {
  const errors: Record<string, string> = {};
  const dimensions = getImageDimensions(templateType, {
    expressivePassEnabled,
  });

  const checkImageDimensions = (
    imageSrc: string,
    maxDimensions: { width: number; height: number } | undefined,
    errorPath: string
  ): Promise<void> => {
    return new Promise((resolve) => {
      if (!imageSrc || !maxDimensions) {
        resolve();
        return;
      }

      const img = document.createElement('img');
      img.onload = () => {
        if (
          img.naturalWidth > maxDimensions.width ||
          img.naturalHeight > maxDimensions.height
        ) {
          errors[errorPath] =
            `Image is too large. Maximum dimensions: ${maxDimensions.width} × ${maxDimensions.height}px`;
        }
        resolve();
      };
      img.onerror = () => {
        resolve();
      };
      img.src = imageSrc;
    });
  };

  const promises: Promise<void>[] = [];

  (['shared', 'apple', 'google'] as Platform[]).forEach((platform) => {
    const platformImages = images[platform] || {};
    Object.keys(platformImages).forEach((field) => {
      const imageSrc = platformImages[field as ImageField];
      if (!imageSrc) return;
      promises.push(
        checkImageDimensions(
          imageSrc,
          dimensions[platform]?.[field as ImageField],
          `images.${platform}.${field}.dimensions`
        )
      );
    });
  });

  if (appleEnhancedDesign?.type != null) {
    (['artwork', 'secondaryLogo', 'venueMap'] as const).forEach((field) => {
      const imageSrc = appleEnhancedDesign.images[field];
      if (!imageSrc) return;
      promises.push(
        checkImageDimensions(
          imageSrc,
          dimensions.enhancedEventTicket?.[field],
          `${APPLE_ENHANCED_DESIGN_IMAGES_NAMESPACE}.${field}.dimensions`
        )
      );
    });
  }

  await Promise.all(promises);
  return errors;
};
